/**
 * useRadiantThemeController — single owner of runtime radiant theming.
 *
 * Consolidates what used to be three separate effects in
 * authenticated-app-layout.tsx (initial brand/mode apply, the
 * data-radiant-theme / nav-bridge reaction, and the appearance-mode +
 * matchMedia listener). Having one owner removes the class of bug where the
 * effects set data-theme / radiantNavMode / brand tokens from different
 * places and disagreed about ordering.
 *
 * Responsibilities:
 *   - Flag OFF  → fully inert (no data-theme, no brand, nav bridge off).
 *   - Flag ON   → resolve the persisted brand color + the user's appearance
 *                 mode (default 'system'), set data-theme, inject brand
 *                 tokens, and keep them in sync with:
 *                   • the appearance dropdown  ('rd:user-theme-changed')
 *                   • the OS scheme            (matchMedia, when mode=system)
 *                   • the admin brand picker   (radiant-theme:apply/clear/nav)
 *
 * Source of truth for the ACTIVE brand color is getCurrentSourceColor()
 * (runtime-applied, reflects an admin change made this session) with the
 * persisted config value as the reload fallback. Re-running on the persisted
 * value means a late-arriving config still themes correctly.
 */
import { blinkConstants } from '@thoughtspot/blink-constants';
import { resolveThemeMode } from '@thoughtspot/custom-style-service';
import { useEffect, useMemo, useState } from 'react';
import { addCustomCssElements } from '/@utils/embed.util';
import { applyTheme, getCurrentSourceColor } from './dynamic-theme/apply-theme';
import { registerDynamicThemeListener } from './dynamic-theme/global-api';
import {
    activateNavThemeBridge,
    deactivateNavThemeBridge,
} from './dynamic-theme/nav-theme-bridge';
import {
    getUserThemeModePref,
    resolveEffectiveDark,
    UserThemeMode,
} from './user-theme-preference';

/** Minimal shape of the services the controller consumes. */
interface CustomStylingServiceLike {
    getConfig: () => { getRawAppPanelColor?: () => string | null } | null;
}
interface SessionServiceLike {
    getUserGuid?: () => string;
}

export interface RadiantThemeControllerParams {
    radiantEnabled: boolean;
    customStylingService: CustomStylingServiceLike;
    sessionService: SessionServiceLike | null | undefined;
    /**
     * Legacy theme-type used when radiant nav mode is inactive. Also serves as
     * a "config loaded" signal — it flips to CUSTOM once a brand color is
     * present, which re-derives the persisted brand color below.
     */
    themeType: string | undefined;
    /** Theme type to fall back to when radiant nav mode is null. */
    leftNavThemeType: string | undefined;
}

export interface RadiantThemeControllerResult {
    /** 'light' | 'dark' | 'brand', or null when the radiant flow is inactive. */
    radiantNavMode: string | null;
    /** Legacy themeType constant mapped from the active radiant nav mode. */
    effectiveThemeType: string | undefined;
}

/** Push applied vars into the embed CSS infra (so iframes / SDK see them). */
function registerAppliedVars(vars: Record<string, string>): void {
    if (Object.keys(vars).length === 0) return;
    addCustomCssElements({
        customizations: { style: { customCSS: { variables: vars } } },
    });
}

export function useRadiantThemeController({
    radiantEnabled,
    customStylingService,
    sessionService,
    themeType,
    leftNavThemeType,
}: RadiantThemeControllerParams): RadiantThemeControllerResult {
    const [radiantNavMode, setRadiantNavMode] = useState<string | null>(null);

    // The persisted brand color (null when none / flag off). Recomputes when
    // themeType flips (i.e. when the style config finishes loading), so a
    // reload reliably re-applies the saved brand color.
    const persistedBrand = useMemo<string | null>(() => {
        if (!radiantEnabled) return null;
        const raw =
            customStylingService.getConfig()?.getRawAppPanelColor?.() || null;
        const resolved = resolveThemeMode(raw, true);
        return resolved.radiant && resolved.mode === 'brand'
            ? resolved.sourceHex ?? null
            : null;
        // themeType is the "config changed" proxy; customStylingService is stable.
    }, [radiantEnabled, themeType]);

    // --- Main theming effect: initial apply + all listeners -----------------
    useEffect(() => {
        if (!radiantEnabled) {
            // Flag OFF: no mode, no brand. The nav-bridge effect below reacts
            // to the null nav mode and tears the bridge down.
            setRadiantNavMode(null);
            return undefined;
        }

        const userId = sessionService?.getUserGuid?.() ?? '';

        let mediaQuery: MediaQueryList | null = null;
        let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;
        const removeMediaListener = () => {
            if (mediaQuery && mediaListener) {
                mediaQuery.removeEventListener('change', mediaListener);
            }
            mediaQuery = null;
            mediaListener = null;
        };

        // Runtime-applied color wins over the (possibly stale) persisted one,
        // so an admin change made this session reapplies the right color on a
        // mode toggle without a reload.
        const activeBrandColor = () =>
            getCurrentSourceColor() || persistedBrand;

        const applyForMode = (isDark: boolean) => {
            if (isDark) {
                document.documentElement.setAttribute('data-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-theme');
            }

            const brand = activeBrandColor();
            if (brand) {
                try {
                    registerAppliedVars(applyTheme(brand, { isDark }));
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(
                        '[dynamic-theme] Failed to apply brand theme',
                        error,
                    );
                }
            }

            let navMode: 'brand' | 'dark' | 'light';
            if (brand) navMode = 'brand';
            else if (isDark) navMode = 'dark';
            else navMode = 'light';
            setRadiantNavMode(navMode);
        };

        const installMediaListenerIfSystem = (mode: UserThemeMode) => {
            if (mode !== 'system') return;
            mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            mediaListener = (e: MediaQueryListEvent) => applyForMode(e.matches);
            mediaQuery.addEventListener('change', mediaListener);
        };

        // Initial apply from stored appearance + persisted brand color.
        const initialMode = getUserThemeModePref(userId);
        applyForMode(resolveEffectiveDark(initialMode));
        installMediaListenerIfSystem(initialMode);

        // Appearance dropdown changed the mode.
        const onUserThemeChange = (event: Event) => {
            const { mode } = (event as CustomEvent).detail ?? {};
            removeMediaListener();
            applyForMode(resolveEffectiveDark(mode as UserThemeMode));
            installMediaListenerIfSystem(mode as UserThemeMode);
        };
        document.addEventListener('rd:user-theme-changed', onUserThemeChange);

        // Admin brand picker (cross-MFE). handleApply/handleClear inside the
        // listener do the applyTheme/clearTheme + embed registration; the nav
        // event keeps radiantNavMode in sync.
        const cleanupBridge = registerDynamicThemeListener({
            onNavModeChange: ({ active, mode }) => {
                setRadiantNavMode(active ? mode || 'light' : null);
            },
        });

        return () => {
            document.removeEventListener(
                'rd:user-theme-changed',
                onUserThemeChange,
            );
            removeMediaListener();
            cleanupBridge();
        };
    }, [radiantEnabled, persistedBrand]);

    // --- Nav bridge reaction: pure function of radiantNavMode ---------------
    useEffect(() => {
        if (radiantNavMode) {
            activateNavThemeBridge();
            // Signal radiant theme to surfaces that gate styling on it
            // (liveboard styling panel/header, color palette popover). Set on
            // <html> so body-portaled popovers inherit it too.
            document.documentElement.setAttribute('data-radiant-theme', '');
        } else {
            deactivateNavThemeBridge();
            document.documentElement.removeAttribute('data-radiant-theme');
        }
    }, [radiantNavMode]);

    const effectiveThemeType = useMemo(() => {
        if (!radiantNavMode) return leftNavThemeType;
        if (radiantNavMode === 'dark') return blinkConstants.themeType.DARK;
        return blinkConstants.themeType.TWO_TONE;
    }, [radiantNavMode, leftNavThemeType]);

    return { radiantNavMode, effectiveThemeType };
}
